import { useState } from 'react';
import { Button } from '@nextgen-web-framework/all';
import {
  CartConstantsEnum,
  cartApi,
  type Cart,
  CartEvents,
  CartEventsEnum,
  updateCart,
  ApiProjections,
  InitialCartState,
  getActiveCart,
  createOrFetchCart,
  SubCartDetail,
  updateRedirectionToHomePage,
} from '@commerce-cart-webcomponents/sdk';
import { StorageService, type AppState, type AppDispatch, ConsoleLogger } from '@nextgen-web-framework/core';
import { useDispatch, useSelector } from 'react-redux';
import { ButtonLoader } from '../../../ui/common/components/cart-loader/button-loader/ButtonLoader';
import { CTYPE } from './types/enums';
import { ConfirmButtonProps } from './types/propsType';

export function ConfirmButton({
  entries,
  subCarts,
  onSuccess = () => null,
  cType = CTYPE.ORDER,
  isMinProjection = false,
  sopEntryIds = [],
  removeSubcartsId = [],
  isChannelPOS = false,
  ...buttonProps
}: ConfirmButtonProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [showLoader, setShowLoader] = useState(false);
  const commerceCartState = useSelector((state: AppState) => state.commerceCart);

  const addToCart = () => {
    const { cart }: InitialCartState = commerceCartState;
    const payload = {
      subCarts: [
        {
          subCartId: cart.subCarts[0]?.subCartId ?? StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
          entries,
        },
      ],
      version: Number(StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion)),
    };

    cartApi
      .addToCart({
        cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
        subCartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
        payload,
        projections: isMinProjection ? ApiProjections.MIN : ApiProjections.DETAILED,
      })
      .then((res) => {
        dispatch(updateCart(res as Cart));
        onSuccess();
        CartEvents.publishEvent({
          event: CartEventsEnum.AddToCart,
          payload: {
            cart: res,
            cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
          },
        });
        CartEvents.publishEvent({
          event: CartEventsEnum.UpdateMiniCartIcon,
          payload: {
            cart: res,
            cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
          },
        });
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  const deleteSopEntries = async () => {
    const { cart }: InitialCartState = commerceCartState;
    const activeCart = getActiveCart(cart, '');
    const res = await cartApi.removeEntryFromCart({
      cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
      subCartId: activeCart?.subCartId,
      entryId: encodeURIComponent(sopEntryIds.join(',')),
    });

    dispatch(updateCart(res as Cart));
    CartEvents.publishEvent({
      event: CartEventsEnum.RemoveCartEntry,
      payload: {
        cart: res,
        cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
      },
    });
    setShowLoader(false);
  };
  const deleteSubcarts = async () => {
    try {
      const res = await cartApi.deleteCart({
        cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
        subCartIds: removeSubcartsId.join(','),
        options: {
          skipCartIdStoreInLS: true,
          skipCartResStoreInLS: true,
          skipVersionStoreInLS: true,
        },
      });
      if (res && !isChannelPOS) {
        await cartApi
          .getSubCart({
            cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
            subCartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
            projections: ApiProjections.MIN,
            options: {
              skipCartIdStoreInLS: true,
              skipCartResStoreInLS: true,
            },
          })
          .then((res: Cart) => {
            StorageService.getLocalStorage().store(CartConstantsEnum.CartReqVersion, res.version);
          });

        CartEvents.publishEvent({
          event: CartEventsEnum.RemoveCartEntry,
          payload: {
            cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
            cart_type: '',
          },
        });
      }
    } catch (e) {
      ConsoleLogger.log('Error occured while deleting subcarts');
    } finally {
      setShowLoader(false);
    }
  };

  const updateCartEntries = async (updatedSubCarts?: SubCartDetail[]) => {
    const payload = {
      subCarts: updatedSubCarts as SubCartDetail[],
      version: Number(StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion)),
    };

    try {
      const res = await cartApi.updateCartEntries({
        cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
        payload,
      });
      dispatch(updateCart(res as Cart));
      CartEvents.publishEvent({
        event: CartEventsEnum.UpdateCartEntryQty,
        payload: {
          cart: res,
          cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
        },
      });
    } catch (e) {
      ConsoleLogger.log('Error occured while updating subcarts');
    } finally {
      setShowLoader(false);
    }
  };

  const onReviewCartClick = async () => {
    // to pass latest version to updateCartEntries added async await on deleteSopEntries
    sopEntryIds?.length && (await deleteSopEntries());

    removeSubcartsId?.length && (await deleteSubcarts());

    subCarts?.length && (await updateCartEntries(subCarts));
    onSuccess();
  };

  const handleClick = async () => {
    setShowLoader(true);
    if (cType === CTYPE.PROMOTION) {
      dispatch(updateRedirectionToHomePage(true));
      onReviewCartClick();
      return;
    }
    const cartIdInLS = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
    if (!cartIdInLS) {
      await createOrFetchCart();
    }
    addToCart();
  };

  return showLoader ? <ButtonLoader size={buttonProps.size} /> : <Button onClick={handleClick} {...buttonProps} />;
}

export { CTYPE };
